import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Image as ImageIcon, ArrowUp, ArrowDown, X, UploadCloud, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_IMAGE_MB = 4;

export default function AdminPortfolio({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/portfolio`);
      setItems(data);
    } catch {
      toast.error("Failed to load portfolio.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const openCreate = () => { setEditing(null); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setOpen(true); };

  const handleSaved = (saved, isCreate) => {
    setItems((list) => {
      if (isCreate) return [saved, ...list].sort((a, b) => a.order - b.order);
      return list.map((x) => (x.id === saved.id ? saved : x)).sort((a, b) => a.order - b.order);
    });
    setOpen(false);
    // Pull fresh server state so order is authoritative
    fetchItems();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this project from the portfolio?")) return;
    try {
      await axios.delete(`${API}/admin/portfolio/${id}`, { headers: { "X-Admin-Token": token } });
      setItems((list) => list.filter((x) => x.id !== id));
      toast.success("Project removed.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const move = async (idx, dir) => {
    const next = [...items];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setItems(next);
    try {
      await axios.post(
        `${API}/admin/portfolio/reorder`,
        { ids: next.map((x) => x.id) },
        { headers: { "X-Admin-Token": token } },
      );
    } catch {
      toast.error("Reorder failed.");
      fetchItems();
    }
  };

  return (
    <div data-testid="admin-portfolio" className="space-y-5">
      <div className="bg-white border border-border rounded-md p-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display font-bold text-lg text-navy">Portfolio Projects</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            These projects appear on the public site. First item is the large feature.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="portfolio-add-btn" className="bg-sunrise hover:bg-sunrise-hover rounded-md">
          <Plus className="w-4 h-4 mr-2" /> Add Project
        </Button>
      </div>

      {loading ? (
        <div className="bg-white border border-border rounded-md p-12 text-center text-muted-foreground">
          <Loader2 className="w-5 h-5 mx-auto animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-border rounded-md p-12 text-center text-muted-foreground">
          <ImageIcon className="w-8 h-8 mx-auto opacity-60 mb-2" />
          No projects yet. Click <strong>Add Project</strong> to upload your first photo.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it, idx) => (
            <div
              key={it.id}
              data-testid={`portfolio-row-${it.id}`}
              className="bg-white border border-border rounded-md overflow-hidden flex flex-col"
            >
              <div className="relative aspect-[4/3] bg-secondary">
                <img src={it.url} alt={it.title} className="w-full h-full object-cover" />
                {idx === 0 && (
                  <span className="absolute top-2 left-2 bg-sunrise text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded">
                    Feature
                  </span>
                )}
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="text-[11px] uppercase tracking-wider text-sunrise font-semibold">{it.category}</div>
                <div className="font-display font-bold text-navy mt-1 line-clamp-1">{it.title}</div>
                <div className="text-xs text-muted-foreground mt-1">Order #{idx + 1}</div>
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => move(idx, -1)} disabled={idx === 0} className="rounded-md h-8 text-xs" data-testid={`portfolio-up-${it.id}`}>
                    <ArrowUp className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="rounded-md h-8 text-xs" data-testid={`portfolio-down-${it.id}`}>
                    <ArrowDown className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(it)} className="rounded-md h-8 text-xs" data-testid={`portfolio-edit-${it.id}`}>
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(it.id)} className="rounded-md h-8 text-xs text-destructive hover:bg-destructive hover:text-white" data-testid={`portfolio-delete-${it.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <EditDialog open={open} setOpen={setOpen} editing={editing} token={token} onSaved={handleSaved} />
    </div>
  );
}

function EditDialog({ open, setOpen, editing, token, onSaved }) {
  const isEdit = !!editing;
  const [form, setForm] = useState({ title: "", category: "", url: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        title: editing?.title || "",
        category: editing?.category || "",
        url: editing?.url || "",
      });
    }
  }, [open, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      toast.error(`Image too large. Max ${MAX_IMAGE_MB}MB.`);
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      set("url", dataUrl);
    } catch {
      toast.error("Could not read file.");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e?.preventDefault();
    if (!form.title || !form.category || !form.url) {
      toast.error("All fields are required.");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const { data } = await axios.patch(
          `${API}/admin/portfolio/${editing.id}`,
          { title: form.title, category: form.category, url: form.url },
          { headers: { "X-Admin-Token": token } },
        );
        toast.success("Project updated.");
        onSaved(data, false);
      } else {
        const { data } = await axios.post(
          `${API}/admin/portfolio`,
          { title: form.title, category: form.category, url: form.url },
          { headers: { "X-Admin-Token": token } },
        );
        toast.success("Project added.");
        onSaved(data, true);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl rounded-md" data-testid="portfolio-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">{isEdit ? "Edit Project" : "Add New Project"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update the project title, category or replace the photo."
              : "Upload a project photo or paste an image URL. The new project will appear at the top of your portfolio."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-navy">Title</span>
              <Input
                data-testid="portfolio-input-title"
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
                className="mt-1 rounded-md"
                placeholder="e.g. Luxury Kitchen Remodel"
              />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-navy">Category</span>
              <Input
                data-testid="portfolio-input-category"
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
                className="mt-1 rounded-md"
                placeholder="e.g. Residential · Remodeling"
              />
            </label>
          </div>

          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-navy">Photo</span>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-[1fr_180px] gap-3">
              <div className="space-y-2">
                <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border bg-secondary hover:bg-secondary/70 rounded-md cursor-pointer py-6 transition-colors" data-testid="portfolio-upload-label">
                  {uploading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  ) : (
                    <>
                      <UploadCloud className="w-6 h-6 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Click to upload (max {MAX_IMAGE_MB}MB)</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    className="hidden"
                    data-testid="portfolio-file-input"
                  />
                </label>
                <div className="text-xs text-muted-foreground text-center">— or paste an image URL —</div>
                <Textarea
                  data-testid="portfolio-input-url"
                  value={form.url.startsWith("data:") ? "[uploaded image]" : form.url}
                  onChange={(e) => set("url", e.target.value)}
                  readOnly={form.url.startsWith("data:")}
                  rows={2}
                  className="rounded-md text-xs"
                  placeholder="https://example.com/photo.jpg"
                />
              </div>
              <div className="aspect-[4/3] bg-secondary border border-border rounded-md overflow-hidden grid place-items-center">
                {form.url ? (
                  <img src={form.url} alt="preview" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-8 h-8 text-muted-foreground/40" />
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-md" data-testid="portfolio-cancel">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button type="submit" disabled={saving} className="bg-sunrise hover:bg-sunrise-hover rounded-md" data-testid="portfolio-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : isEdit ? "Save Changes" : "Add Project"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
